/**
 * Bonne Maniere Custom JS
 * Handles all lh- prefixed sections behavior
 * 
 * Features:
 * - Horizontal Scroll Navigation (Arrows)
 * - Video Cards (TikTok style)
 * - Logos Showcase (Infinite Scroll)
 */

(function() {
    'use strict';
  
    // --- Utilities ---
  
    function scrollWrapper(wrapper, amount) {
      if (!wrapper) return;
      // Use a more robust animation logic or check browser compatibility if needed
      // But for now, ensure behavior is smooth.
      // If native smooth scroll is jerky, it might be due to large layout shifts or heavy content.
      wrapper.scrollBy({ left: amount, behavior: 'smooth' });
    }
  
    // --- 1. Generic Horizontal Scroll Navigation ---
    // Targets any section with .lh-nav-arrow and a scrollable wrapper
    function initScrollNavigation(root) {
      if (!root) return;

      // Skip if this is a custom element that manages its own scrolling
      // This prevents conflict where this script clones buttons and destroys Web Component listeners
      const selfManagedTags = [
        'LH-BLOG-INSPIRATIONS', 
        'LH-AVIS-VERIFIES', 
        'LH-LATEST-HIGHLIGHT', 
        'LH-FEATURED-COLLECTION'
      ];
      
      if (selfManagedTags.includes(root.tagName.toUpperCase())) {
        return;
      }
  
      // Identify the wrapper
      const wrapper = root.querySelector('.lh-horizontal-scroll') || 
                      root.querySelector('.lh-cards-wrapper') || 
                      root.querySelector('.lh-cards-scroller');
                      
      if (!wrapper) return;

      // CRITICAL: Check if the found wrapper is inside a self-managed custom element
      // This prevents the script from hijacking elements inside our Web Components
      // (which happens when the section wrapper div has the same targeting class)
      const protectedSelector = selfManagedTags.map(t => t.toLowerCase()).join(',');
      if (wrapper.closest(protectedSelector)) {
        return;
      }

      const prevBtn = root.querySelector('.lh-prev-arrow');
      const nextBtn = root.querySelector('.lh-next-arrow');
      
      // Determine scroll amount (Default 300 or card width)
      let scrollAmount = 300;
      const firstCard = wrapper.firstElementChild || wrapper.querySelector('.lh-category-card') || wrapper.querySelector('.lh-card');
      if (firstCard) {
          // Get width including margin if possible, or just offsetWidth + gap approximation
          scrollAmount = firstCard.offsetWidth + 20; 
      }
  
      // Remove existing listeners to avoid duplicates (if using named functions)
      // But here we are using anonymous, so we rely on init running once per page load.
      // For re-init (design mode), we might pile up listeners if not careful.
      // Using { once: true } is an option but we want multiple clicks.
      // We'll assume a clean slate or that 'click' is cheap enough.
      
      if (prevBtn) {
        // Clone to remove old listeners
        const newPrev = prevBtn.cloneNode(true);
        prevBtn.parentNode.replaceChild(newPrev, prevBtn);
        newPrev.addEventListener('click', (e) => {
          e.preventDefault();
          scrollWrapper(wrapper, -scrollAmount);
        });
      }
  
      if (nextBtn) {
        const newNext = nextBtn.cloneNode(true);
        nextBtn.parentNode.replaceChild(newNext, nextBtn);
        newNext.addEventListener('click', (e) => {
          e.preventDefault();
          scrollWrapper(wrapper, scrollAmount);
        });
      }
    }
  
    // --- 2. Video Cards (TikTok Style) ---
    function initVideoCards() {
      const cards = document.querySelectorAll('.lh-js-video-card');
      cards.forEach(function(card) {
        if (card.dataset.lhVideoInit === 'true') return;
        card.dataset.lhVideoInit = 'true';

        card.addEventListener('click', function() {
          const video = card.querySelector('video');
          if (!video) return;

          const deferredSrc = video.getAttribute('data-src');
          if (deferredSrc && !video.getAttribute('src')) {
            video.setAttribute('src', deferredSrc);
            video.load();
          }

          if (video.paused) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise.then(function() {
                card.classList.add('lh-is-playing');
              }).catch(function() {});
            } else {
              card.classList.add('lh-is-playing');
            }
          } else {
            video.pause();
            card.classList.remove('lh-is-playing');
          }
        });
      });
    }
  
    // --- 3. Product Card Tooltips ---
    window.initProductCardTooltips = function(root) {
      if (!root) root = document;
      
      const tooltipWrappers = root.querySelectorAll('.lh-product-card-tooltip-wrapper');
      
      tooltipWrappers.forEach(function(wrapper) {
        if (wrapper.dataset.tooltipInitialized === 'true') return;
        wrapper.dataset.tooltipInitialized = 'true';
        
        const textElement = wrapper.querySelector('h3');
        const tooltip = wrapper.querySelector('.lh-product-card-tooltip');
        
        if (!textElement || !tooltip) return;
        
        function checkTruncation() {
          const isTruncated = textElement.scrollHeight > textElement.clientHeight;
          
          if (isTruncated) {
            wrapper.setAttribute('data-tooltip-enabled', 'true');
          } else {
            wrapper.removeAttribute('data-tooltip-enabled');
          }
        }
        
        checkTruncation();
        
        let resizeTimeout;
        window.addEventListener('resize', function() {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(checkTruncation, 100);
        });
      });
    };
  
    // --- 4. Logos Showcase Logic ---
    function initLogosShowcase(section) {
      const wrapper = section.querySelector('.lh-logos-showcase-wrapper');
      const track = section.querySelector('.lh-logos-showcase-track');
      
      if (!wrapper || !track) return;
  
      // Prevent double init
      if (wrapper.dataset.lhInitialized) return;
      wrapper.dataset.lhInitialized = 'true';
  
      const scrollSpeed = parseFloat(wrapper.dataset.scrollSpeed || 1);
      const logoGap = parseFloat(wrapper.dataset.logoGap || 40);
      let isPaused = false;
  
      const logos = track.querySelectorAll('.lh-logo-item');
      if (logos.length > 0) {
        const images = track.querySelectorAll('img');
        let loadedImages = 0;
        
        const checkImages = () => {
           if (loadedImages === images.length || images.length === 0) {
             updateAnimation();
           }
        };
  
        if (images.length > 0) {
          images.forEach(img => {
            if (img.complete) {
              loadedImages++;
            } else {
              img.addEventListener('load', function() {
                loadedImages++;
                checkImages();
              });
            }
          });
          checkImages();
        } else {
          updateAnimation();
        }
      }
  
      function updateAnimation() {
        const logoCount = Math.floor(logos.length / 2);
        const firstSet = Array.from(logos).slice(0, logoCount);
        
        let totalWidth = 0;
        firstSet.forEach(logo => {
          const rect = logo.getBoundingClientRect();
          totalWidth += rect.width;
        });
        
        if (logoCount > 1) {
          totalWidth += logoGap * (logoCount - 1);
        }
        
        const baseDuration = (totalWidth || 2000) / (50 * scrollSpeed);
        track.style.animationDuration = `${Math.max(10, baseDuration)}s`;
      }
  
      wrapper.addEventListener('mouseenter', function() {
        isPaused = true;
        track.style.animationPlayState = 'paused';
      });
  
      wrapper.addEventListener('mouseleave', function() {
        isPaused = false;
        track.style.animationPlayState = 'running';
      });
  
      document.addEventListener('visibilitychange', function() {
        if (document.hidden) {
          track.style.animationPlayState = 'paused';
        } else {
          track.style.animationPlayState = isPaused ? 'paused' : 'running';
        }
      });
    }
  
  
    // --- Master Init ---
    function initAll() {
      // 1. Scroll Navigation
      // We target specific known section classes or any container with our arrow classes
      const scrollSections = document.querySelectorAll('.index-section, .lh-section-overflow-fix, .lh-index-section'); 
      // Also include specific ones just in case
      const specificSections = document.querySelectorAll('.lh-reviews-section, .lh-blog-inspi-section');
      
      scrollSections.forEach(initScrollNavigation);
      specificSections.forEach(initScrollNavigation);
  
      // 2. Videos
      initVideoCards();

      // 3. Product Card Tooltips
      initProductCardTooltips();

      // 4. Logos
      // Find all wrappers and init their containers
      const logoWrappers = document.querySelectorAll('.lh-logos-showcase-wrapper');
      logoWrappers.forEach(wrapper => {
        const section = wrapper.closest('.index-section') || wrapper.parentElement;
        if(section) initLogosShowcase(section);
      });
    }
  
    // --- Lifecycle ---
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  
    // --- Shopify Design Mode ---
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function(event) {
        const section = event.target;
        
        // Try to init scroll nav
        initScrollNavigation(section);
        
        // Try to init videos
        // (Note: querySelectorAll inside section would be better for perf but initVideoCards() selects globally. 
        // Since it's rare, global re-scan is fine or we can optimize)
        initVideoCards();
        
        // Try to init logos
        if (section.querySelector('.lh-logos-showcase-wrapper')) {
           initLogosShowcase(section);
        }
      });
      
      document.addEventListener('shopify:section:unload', function(event) {
         // Cleanup if needed
      });
    }
  
  })();

